import { useState, useMemo, useRef, useCallback, useEffect } from "react";

export function LineChart({
  data,
  title,
  valuePrefix = "",
  valueSuffix = "",
  color = "#25D366",
  emptyMessage = "Aucune donnée",
  formatValue,
}) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0) setDims({ w: width, h: height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const marginR = 22;
  const marginB = 20;
  const chartW = dims.w - marginR;
  const chartH = dims.h - marginB;

  const values = useMemo(() => data.map((d) => d.value), [data]);
  const total = useMemo(() => values.reduce((s, v) => s + v, 0), [values]);
  const max = useMemo(() => {
    const dataMax = Math.max(...values, 0);
    if (dataMax === 0) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(dataMax)));
    const niceSteps = [1, 1.5, 2, 2.5, 3, 4, 5, 7.5, 10];
    const normalized = dataMax / magnitude;
    const niceMax = (niceSteps.find((s) => s >= normalized) || 10) * magnitude;
    return niceMax;
  }, [values]);

  const topPad = 10;
  const points = useMemo(
    () =>
      values.map((v, i) => ({
        x: values.length > 1 ? (i / (values.length - 1)) * chartW : chartW / 2,
        y: topPad + (chartH - topPad) - (v / max) * (chartH - topPad),
      })),
    [values, max, chartW, chartH]
  );

  const pathD = useMemo(
    () =>
      points.reduce((acc, p, i) => {
        if (i === 0) return `M ${p.x} ${p.y}`;
        const prev = points[i - 1];
        const cpx = (prev.x + p.x) / 2;
        return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
      }, ""),
    [points]
  );

  const fillD = useMemo(
    () =>
      points.length > 0
        ? `${pathD} L ${points[points.length - 1].x} ${chartH} L ${points[0].x} ${chartH} Z`
        : "",
    [pathD, points, chartH]
  );

  const ySteps = 4;
  const yLabels = useMemo(
    () => Array.from({ length: ySteps + 1 }, (_, i) => Math.round((max / ySteps) * i)),
    [max]
  );

  const labeledIndices = useMemo(() => {
    if (data.length <= 7) return data.map((_, i) => i);
    const step = Math.ceil(data.length / 6);
    const indices = [0];
    for (let i = step; i < data.length - 1; i += step) indices.push(i);
    indices.push(data.length - 1);
    return indices;
  }, [data]);

  const handleMouseMove = useCallback(
    (e) => {
      const svg = svgRef.current;
      if (!svg || points.length === 0) return;
      const rect = svg.getBoundingClientRect();
      const svgX = ((e.clientX - rect.left) / rect.width) * dims.w;
      let nearest = 0;
      let minDist = Infinity;
      points.forEach((p, i) => {
        const d = Math.abs(p.x - svgX);
        if (d < minDist) {
          minDist = d;
          nearest = i;
        }
      });
      setHoveredIdx(nearest);
    },
    [points, dims.w]
  );

  const fmtValue = useCallback(
    (v) => {
      if (formatValue) return formatValue(v);
      return `${valuePrefix}${v.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}${valueSuffix}`;
    },
    [formatValue, valuePrefix, valueSuffix]
  );

  const fmtYLabel = useCallback(
    (v) => {
      if (v >= 1000) return `${valuePrefix}${Math.round(v / 1000)}k${valueSuffix}`;
      return `${valuePrefix}${Math.round(v)}${valueSuffix}`;
    },
    [valuePrefix, valueSuffix]
  );

  const gradientId = `lineChartFill_${color.replace("#", "")}`;

  return (
    <div className="flex flex-col gap-1 w-full h-full overflow-hidden">
      <div className="flex flex-row justify-between items-end shrink-0">
        <span className="text-h2 font-semibold" style={{ color: "#374151" }}>
          {title}
        </span>
      </div>

      {total === 0 ? (
        <div
          className="flex items-center justify-center text-h3 flex-1"
          style={{ color: "#D1D5DB" }}
        >
          {emptyMessage}
        </div>
      ) : (
        <div ref={containerRef} className="flex-1 w-full min-h-0 overflow-hidden">
          {dims.w > 0 && (
            <svg
              ref={svgRef}
              width={dims.w}
              height={dims.h}
              viewBox={`0 0 ${dims.w} ${dims.h}`}
              onMouseMove={handleMouseMove}
              onMouseLeave={() => setHoveredIdx(null)}
              style={{ cursor: "crosshair", display: "block" }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={color} stopOpacity="0.01" />
                </linearGradient>
              </defs>
              {yLabels.map((label, i) => {
                const y = topPad + (chartH - topPad) - (label / max) * (chartH - topPad);
                return (
                  <g key={i}>
                    <line x1={0} y1={y} x2={chartW} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                    <text x={chartW + 6} y={y + 4} fontSize={10} fill="#a3a3a3">
                      {fmtYLabel(label)}
                    </text>
                  </g>
                );
              })}
              {fillD && <path d={fillD} fill={`url(#${gradientId})`} />}
              {pathD && (
                <path d={pathD} fill="none" stroke={color} strokeWidth={2.5} />
              )}
              {hoveredIdx !== null && points[hoveredIdx] && (
                <>
                  <line
                    x1={points[hoveredIdx].x} y1={topPad}
                    x2={points[hoveredIdx].x} y2={chartH}
                    stroke="#e5e7eb" strokeWidth={1} strokeDasharray="4 2"
                  />
                  <circle
                    cx={points[hoveredIdx].x} cy={points[hoveredIdx].y}
                    r={5} fill={color} stroke="white" strokeWidth={2}
                  />
                  <g
                    transform={`translate(${Math.min(Math.max(points[hoveredIdx].x - 60, 0), chartW - 120)}, ${points[hoveredIdx].y < 60 ? points[hoveredIdx].y + 15 : points[hoveredIdx].y - 50})`}
                  >
                    <rect width={120} height={40} rx={8} fill="#1a1a1a" />
                    <text x={60} y={16} textAnchor="middle" fontSize={11} fill="#c8c8c8">
                      {data[hoveredIdx].tooltipLabel}
                    </text>
                    <text x={60} y={32} textAnchor="middle" fontSize={13} fill="white" fontWeight="600">
                      {fmtValue(data[hoveredIdx].value)}
                    </text>
                  </g>
                </>
              )}
              {labeledIndices.map((idx) => (
                <text
                  key={idx}
                  x={values.length > 1 ? (idx / (values.length - 1)) * chartW : chartW / 2}
                  y={chartH + 15}
                  textAnchor={idx === 0 ? "start" : idx === data.length - 1 ? "end" : "middle"}
                  fontSize={10}
                  fill="#a3a3a3"
                  style={{ textTransform: "uppercase" }}
                >
                  {data[idx].label}
                </text>
              ))}
            </svg>
          )}
        </div>
      )}
    </div>
  );
}
